import { StyleSheet, View, TouchableOpacity } from "react-native";
import React, { useState, useEffect, useCallback } from "react";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { FontAwesome } from "@expo/vector-icons";
import { ThemedKebabMenu, type IKebabActionItem } from "@/components/ThemedKebabMenu";

import { useFirebase } from "@components/firebase/useFirebase";
import { useAuth } from "@components/auth/useAuth";
import {
  getUsersFunc,
  updateUserFunc,
  ensureAllContactsGroup,
  getUser,
  addUserClaimFunc,
  removeUserClaimFunc,
} from "@/services/firebase/user-functions";
import { getAllGroups } from "@/services/firebase/group-functions";
import { CreateUserModal } from "@/components/admin/CreateUserModal";
import { UserCard } from "@/components/admin/UserCard";
import { AppUserModel, GroupModel, IUserStorageQuotaSummary, DEFAULT_STORAGE_QUOTA_BYTES, formatBytes } from "common247";
import { useThemeColor } from "@hooks/useThemeColor";
import { BlueLinenRange, BrandColors } from "@/constants/ThemeColours";
import { ThemedList, type ISortOption, type IGroupOption, type ThemedListViewMode } from "@/components/ThemedList";

type UserWithGroup = AppUserModel & {
  allContactsGroup?: GroupModel;
};

const USER_SORT_OPTIONS: ISortOption<UserWithGroup>[] = [
  { key: "displayName", label: "Name", getValue: (u) => u.displayName || u.email || "" },
  { key: "email", label: "Email", getValue: (u) => u.email || "" },
  { key: "createdAt", label: "Created Date", getValue: (u) => u.createdAt || 0 },
  { key: "lastSignInTime", label: "Last Logged On", getValue: (u) => u.lastSignInTime || 0 },
  { key: "authProvider", label: "Auth Provider", getValue: (u) => (u.authProvider === "google" ? "Google IDP" : "User/Pass") },
  { key: "status", label: "Account Status", getValue: (u) => (u.active !== false && !u.disabled ? "Active" : "Deactivated") },
  { key: "userType", label: "Account Type", getValue: (u) => u.userType || "user" },
  { key: "storageQuota", label: "Storage Quota", getValue: (u) => (u as any).storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES },
];

const USER_GROUP_OPTIONS: IGroupOption<UserWithGroup>[] = [
  {
    key: "authProvider",
    label: "Auth Provider",
    getGroupValue: (u) => (u.authProvider === "google" ? "Google IDP" : "User / Pass"),
  },
  {
    key: "lastLoggedOn",
    label: "Last Logged On",
    getGroupValue: (u) => (u.lastSignInTime ? "Previously Logged In" : "Never Logged In"),
  },
  {
    key: "status",
    label: "Account Status",
    getGroupValue: (u) => (u.active !== false && !u.disabled ? "Active Accounts" : "Deactivated Accounts"),
  },
  {
    key: "userType",
    label: "Account Type",
    getGroupValue: (u) => (u.userType === "serviceaccount" ? "Service Accounts" : "Standard Users"),
  },
  {
    key: "hasAllContactsGroup",
    label: "All Contacts Group",
    getGroupValue: (u) => (u.allContactsGroupId ? "Linked All-Contacts Group" : "No Group Linked"),
  },
  {
    key: "quota",
    label: "Storage Quota",
    getGroupValue: (u) => {
      const quotaBytes = (u as any).storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES;
      const gb = ((quotaBytes || 0) / (1024 * 1024 * 1024)).toFixed(0);
      return `${gb} GB Quota`;
    },
  },
  {
    key: "role",
    label: "Primary Role",
    getGroupValue: (u) => {
      const claims = u.claims as Record<string, any> | undefined;
      if (claims?.admin) return "Administrators";
      if (claims?.tester) return "Testers";
      if (claims?.maintainer) return "Maintainers";
      if (claims?.viewer) return "Viewers";
      return "Standard Users";
    },
  },
];

/**
 * Role presets available for administrative assignment on user accounts.
 */
interface IRolePreset {
  key: string;
  label: string;
}

const ROLE_PRESETS: IRolePreset[] = [
  { key: "admin", label: "Admin" },
  { key: "user", label: "User" },
  { key: "viewer", label: "Viewer" },
  { key: "tester", label: "Tester" },
  { key: "maintainer", label: "Maintainer" },
  { key: "print", label: "Printer" },
];

/**
 * Main User Admin screen displaying the directory of users and their claims.
 * Aligned with the Archival Heirloom Aesthetic and dual-system typography.
 *
 * @returns {React.JSX.Element} The rendered User Admin screen.
 */
export default function UserAdminScreen(): React.JSX.Element {
  const services = useFirebase();
  useAuth(true);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const backgroundElement = useThemeColor({}, "backgroundElement");
  const inputReadOnlyBg = useThemeColor({}, "inputReadOnly");
  const inputReadOnlyBorder = useThemeColor({}, "inputReadOnlyBorder");
  const inputReadOnlyTextColor = useThemeColor({}, "inputReadOnlyText");

  const router = useRouter();
  const { userId: paramUserId, create: paramCreate } = useLocalSearchParams<{
    userId?: string;
    create?: "user" | "serviceaccount";
  }>();

  const [users, setUsers] = useState<UserWithGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserWithGroup | null>(null);
  const [editQuotaOnOpen, setEditQuotaOnOpen] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState<boolean>(() => Boolean(paramCreate));
  const [createModalType, setCreateModalType] = useState<"user" | "serviceaccount">(() =>
    paramCreate === "serviceaccount" ? "serviceaccount" : "user"
  );

  // Sync selected user from URL param (?userId=) when user list is loaded or URL changes
  useEffect(() => {
    if (paramUserId && users.length > 0) {
      const match = users.find((u) => u.userId === paramUserId);
      if (match && (!selectedUser || selectedUser.userId !== match.userId)) {
        setSelectedUser(match);
      }
    } else if (!paramUserId && selectedUser) {
      setSelectedUser(null);
    }
  }, [paramUserId, users]);

  // Sync creation modal from URL param (?create=user|serviceaccount)
  useEffect(() => {
    if (paramCreate === "user" || paramCreate === "serviceaccount") {
      setCreateModalType(paramCreate);
      setShowCreateUserModal(true);
    } else if (!paramCreate && showCreateUserModal && !selectedUser) {
      // If cleared via navigation, keep aligned
    }
  }, [paramCreate]);

  const handleSelectUser = useCallback(
    (user: UserWithGroup | null, editQuota = false) => {
      setSelectedUser(user);
      setEditQuotaOnOpen(editQuota);
      const uid = user?.userId;
      router.setParams({ userId: uid || "" });
    },
    [router]
  );

  const handleOpenCreateModal = useCallback(
    (type: "user" | "serviceaccount") => {
      setCreateModalType(type);
      setShowCreateUserModal(true);
      router.setParams({ create: type });
    },
    [router]
  );

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateUserModal(false);
    router.setParams({ create: "" });
  }, [router]);

  /**
   * Fetches the users and their linked All Contacts groups from the backend.
   *
   * @returns {Promise<void>}
   */
  const fetchData = async (): Promise<void> => {
    if (!services) return;
    setLoading(true);
    try {
      const [usersData, groupsResponse] = await Promise.all([
        getUsersFunc(services),
        getAllGroups(services, {}),
      ]);

      const groups = groupsResponse.status ? groupsResponse.groups : [];

      const enriched: UserWithGroup[] = usersData.map((u) => {
        const allContactsGroup = u.allContactsGroupId
          ? groups.find((g) => g.id === u.allContactsGroupId)
          : undefined;
        return { ...u, allContactsGroup };
      });

      setUsers(enriched);
    } catch (error) {
      console.error("Error fetching user directory:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching the user directory.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Triggers group creation and linking for a user by calling ensureAllContactsGroup.
   * Reloads the user directory data after success.
   *
   * @param {string} uid - The unique identifier of the user.
   * @returns {Promise<void>}
   */
  const handleCreateGroup = async (uid?: string): Promise<void> => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not initialized.");
      return;
    }
    if (!uid) {
      CustomAlert.alert("Error", "User identifier is missing.");
      return;
    }
    setLoading(true);
    try {
      const res = await ensureAllContactsGroup(services, uid);
      if (res.status === true) {
        await fetchData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to create All Contacts group.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      fetchData();
    }, [services]),
  );

  /**
   * Toggles the active/deactivated status of a user account.
   *
   * @param {UserWithGroup} targetUser - The user account to toggle.
   * @param {boolean} targetActive - True to activate, false to deactivate.
   * @returns {Promise<void>}
   */
  const handleToggleUserStatus = async (targetUser: UserWithGroup, targetActive: boolean): Promise<void> => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not initialized.");
      return;
    }
    const targetId = targetUser.userId;
    if (!targetId) {
      CustomAlert.alert("Error", "User identifier (userId) is missing.");
      return;
    }
    setLoading(true);
    try {
      const res = await updateUserFunc(services, {
        userId: targetId,
        active: targetActive,
        disabled: !targetActive,
      });
      if (res.status) {
        setUsers((prev) =>
          prev.map((u) =>
            u.userId === targetId
              ? {
                  ...u,
                  active: targetActive,
                  disabled: !targetActive,
                  deactivated: targetActive ? 0 : Date.now(),
                }
              : u
          )
        );
        if (selectedUser && selectedUser.userId === targetId) {
          setSelectedUser((prev) =>
            prev
              ? {
                  ...prev,
                  active: targetActive,
                  disabled: !targetActive,
                  deactivated: targetActive ? 0 : Date.now(),
                }
              : null
          );
        }
        CustomAlert.alert(
          "Success",
          `User ${targetUser.displayName || targetUser.email || targetId} has been ${targetActive ? "reactivated" : "deactivated"} successfully.`
        );
      } else {
        CustomAlert.alert("Error", res.error || `Failed to ${targetActive ? "reactivate" : "deactivate"} user.`);
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Prompts the administrator for confirmation before toggling user active/deactivated status.
   *
   * @param {UserWithGroup} targetUser - The user account to toggle.
   * @param {boolean} targetActive - True to activate, false to deactivate.
   * @returns {void}
   */
  const confirmToggleStatus = (targetUser: UserWithGroup, targetActive: boolean): void => {
    const actionLabel = targetActive ? "Reactivate" : "Deactivate";
    const userName = targetUser.displayName || targetUser.email || targetUser.userId;
    CustomAlert.alert(
      `${actionLabel} User`,
      targetActive
        ? `Are you sure you want to reactivate ${userName}? Their account access will be restored.`
        : `Are you sure you want to deactivate ${userName}? Their session will be revoked and they will not be able to log in.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: actionLabel,
          style: targetActive ? "default" : "destructive",
          onPress: () => {
            void handleToggleUserStatus(targetUser, targetActive);
          },
        },
      ]
    );
  };

  /**
   * Renders an archival card for an individual user in the directory ledger using UserCard.
   *
   * @param {UserWithGroup} item - The user record to render.
   * @param {number} _index - Position index.
   * @param {boolean} _isHovered - Hover state.
   * @param {ThemedListViewMode} [viewMode="medium"] - Active view tier.
   * @returns {React.JSX.Element}
   */
  const renderItem = (
    item: UserWithGroup,
    _index: number,
    _isHovered: boolean,
    viewMode: ThemedListViewMode = "medium"
  ): React.JSX.Element => {
    return (
      <UserCard
        user={item}
        viewMode={viewMode}
        onEdit={(u) => {
          handleSelectUser(u, false);
        }}
        onEditQuota={(u) => {
          handleSelectUser(u, true);
        }}
        onToggleStatus={(u, targetActive) => {
          confirmToggleStatus(u, targetActive);
        }}
        onCreateGroup={(uid) => {
          handleCreateGroup(uid);
        }}
      />
    );
  };

  if (selectedUser) {
    return (
      <ThemedScreen
        title="Edit User"
        leftIcon={{
          name: "arrow-back",
          onPress: () => {
            handleSelectUser(null, false);
            fetchData();
          },
        }}
      >
        <UserEditor
          user={selectedUser}
          services={services}
          initialEditQuota={editQuotaOnOpen}
          onClose={() => {
            handleSelectUser(null, false);
            fetchData();
          }}
          onToggleStatus={handleToggleUserStatus}
        />
      </ThemedScreen>
    );
  }

  return (
    <ThemedScreen title="User Admin" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${users.length} REGISTERED ${users.length === 1 ? "RECORD" : "RECORDS"}`}
        </ThemedText>

        <ThemedList<UserWithGroup>
          data={users}
          sortOptions={USER_SORT_OPTIONS}
          defaultSortKey="displayName"
          defaultSortDirection="asc"
          groupOptions={USER_GROUP_OPTIONS}
          defaultGroupKey="userType"
          persistenceKey="admin-users"
          pageSize={24}
          filterKeys={["displayName", "email", "userId", "uid", "userType"]}
          filterPlaceholder="Search all users..."
          loading={loading}
          onRefresh={() => fetchData()}
          refreshing={loading}
          emptyText="No registered users found."
          contentContainerStyle={styles.listContent}
          toolbarRightComponent={
            <View style={{ flexDirection: "row", gap: 6 }}>
              <ThemedButton
                title="+ User"
                variant="primary"
                onPress={() => handleOpenCreateModal("user")}
              />
              <ThemedButton
                title="+ Service"
                variant="secondary"
                onPress={() => handleOpenCreateModal("serviceaccount")}
              />
            </View>
          }
          renderItem={(item, index, isHovered, viewMode) => renderItem(item, index, isHovered, viewMode)}
        />
      </ThemedView>

      <CreateUserModal
        visible={showCreateUserModal}
        initialAccountType={createModalType}
        onClose={handleCloseCreateModal}
        onUserCreated={() => fetchData()}
      />
    </ThemedScreen>
  );
}



interface IUserEditorProps {
  user: UserWithGroup;
  services: any;
  initialEditQuota?: boolean;
  onClose: () => void;
  onToggleStatus?: (targetUser: UserWithGroup, targetActive: boolean) => Promise<void>;
}

/**
 * Preset quota tiers with monthly prices for rapid allocation.
 */
const QUOTA_TIER_PRESETS: Array<{ label: string; gb: number }> = [
  { label: "2 GB (Free)", gb: 2 },
  { label: "50 GB ($1/mo)", gb: 50 },
  { label: "100 GB ($2/mo)", gb: 100 },
  { label: "500 GB ($5/mo)", gb: 500 },
  { label: "1 TB ($8/mo)", gb: 1024 },
  { label: "2 TB ($15/mo)", gb: 2048 },
];

/**
 * Editor sub-component for managing individual user attributes, groups, and claims.
 * Employs inline form validation and archival heirloom cards.
 *
 * @param {IUserEditorProps} props - Component properties.
 * @returns {React.JSX.Element}
 */
function UserEditor({ user: initialUser, services, initialEditQuota = false, onClose, onToggleStatus }: IUserEditorProps): React.JSX.Element {
  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const backgroundElement = useThemeColor({}, "backgroundElement");
  const inputReadOnlyBg = useThemeColor({}, "inputReadOnly");
  const inputReadOnlyBorder = useThemeColor({}, "inputReadOnlyBorder");
  const inputReadOnlyTextColor = useThemeColor({}, "inputReadOnlyText");

  const [currentUser, setCurrentUser] = useState<UserWithGroup>(initialUser);
  const [displayName, setDisplayName] = useState(initialUser.displayName || "");
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [betaGroup, setBetaGroup] = useState<string>(initialUser.betaGroup || "");
  const [betaGroupError, setBetaGroupError] = useState<string | null>(null);
  const [savingBetaGroup, setSavingBetaGroup] = useState(false);

  const isUserActive = currentUser.active !== false && !currentUser.disabled;

  const handleToggleCurrentStatus = async (targetActive: boolean): Promise<void> => {
    if (onToggleStatus) {
      await onToggleStatus(currentUser, targetActive);
      setCurrentUser((prev) => ({
        ...prev,
        active: targetActive,
        disabled: !targetActive,
        deactivated: targetActive ? 0 : Date.now(),
      }));
    } else {
      if (!services) return;
      const targetId = currentUser.userId;
      if (!targetId) {
        CustomAlert.alert("Error", "User identifier (userId) is missing.");
        return;
      }
      setLoading(true);
      try {
        const res = await updateUserFunc(services, {
          userId: targetId,
          active: targetActive,
          disabled: !targetActive,
        });
        if (res.status) {
          setCurrentUser((prev) => ({
            ...prev,
            active: targetActive,
            disabled: !targetActive,
            deactivated: targetActive ? 0 : Date.now(),
          }));
          CustomAlert.alert(
            "Success",
            `User ${currentUser.displayName || currentUser.email || targetId} has been ${targetActive ? "reactivated" : "deactivated"} successfully.`
          );
        } else {
          CustomAlert.alert("Error", res.error || `Failed to ${targetActive ? "reactivate" : "deactivate"} user.`);
        }
      } catch (err: any) {
        CustomAlert.alert("Error", err.message || "An error occurred.");
      } finally {
        setLoading(false);
      }
    }
  };

  const confirmToggleStatus = (targetActive: boolean): void => {
    const actionLabel = targetActive ? "Reactivate" : "Deactivate";
    const userName = currentUser.displayName || currentUser.email || currentUser.userId;
    CustomAlert.alert(
      `${actionLabel} User`,
      targetActive
        ? `Are you sure you want to reactivate ${userName}? Their account access will be restored.`
        : `Are you sure you want to deactivate ${userName}? Their session will be revoked and they will not be able to log in.`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: actionLabel,
          style: targetActive ? "default" : "destructive",
          onPress: () => {
            void handleToggleCurrentStatus(targetActive);
          },
        },
      ]
    );
  };

  const rawInitialQuota = (initialUser as any).storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES;
  const initialQuotaBytes = typeof rawInitialQuota === "number" && !isNaN(rawInitialQuota) ? rawInitialQuota : DEFAULT_STORAGE_QUOTA_BYTES;
  const [quotaSummary, setQuotaSummary] = useState<IUserStorageQuotaSummary | null>(null);
  const [quotaGb, setQuotaGb] = useState<string>(
    (((initialQuotaBytes || 0) / (1024 * 1024 * 1024))).toFixed(1)
  );
  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [savingQuota, setSavingQuota] = useState(false);
  const [isEditingQuota, setIsEditingQuota] = useState(initialEditQuota);

  React.useEffect(() => {
    if (initialEditQuota) {
      setIsEditingQuota(true);
    }
  }, [initialEditQuota]);

  const [claimKey, setClaimKey] = useState("");
  const [claimKeyError, setClaimKeyError] = useState<string | null>(null);
  const [claimValue, setClaimValue] = useState("true");

  const [loading, setLoading] = useState(false);

  /**
   * Refreshes user details and storage quota from the backend using the new getUser API.
   *
   * @returns {Promise<void>}
   */
  const refreshUserData = async (): Promise<void> => {
    const targetId = currentUser.userId;
    if (!targetId) return;
    setLoading(true);
    try {
      const res = await getUser(services, { userId: targetId });
      if (res.status && res.user) {
        const updated = res.user;
        let allContactsGroup = currentUser.allContactsGroup;
        if (updated.allContactsGroupId && (!allContactsGroup || allContactsGroup.id !== updated.allContactsGroupId)) {
          try {
            const groupsRes = await getAllGroups(services, {});
            if (groupsRes.status && groupsRes.groups) {
              allContactsGroup = groupsRes.groups.find((g) => g.id === updated.allContactsGroupId);
            }
          } catch (gErr) {
            console.error("Error fetching group:", gErr);
          }
        }
        setCurrentUser({ ...updated, allContactsGroup });
        setDisplayName(updated.displayName || "");
        setBetaGroup(updated.betaGroup || "");
        if (res.quota) {
          setQuotaSummary(res.quota);
          const qBytes = res.quota.storageQuotaBytes ?? (res.quota as any).quotaBytes ?? updated.storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES;
          setQuotaGb((((qBytes || 0) / (1024 * 1024 * 1024))).toFixed(1));
        } else if (updated.storageQuota !== undefined) {
          const qBytes = updated.storageQuota ?? DEFAULT_STORAGE_QUOTA_BYTES;
          setQuotaGb((((qBytes || 0) / (1024 * 1024 * 1024))).toFixed(1));
        }
      }
    } catch (error) {
      console.error("Error refreshing editor user data:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refreshUserData();
  }, [currentUser.userId]);

  /**
   * Updates the user's display name with inline validation.
   *
   * @returns {Promise<void>}
   */
  const handleUpdateName = async (): Promise<void> => {
    if (!displayName.trim()) {
      setDisplayNameError("Display name cannot be empty.");
      return;
    }
    setDisplayNameError(null);
    const targetId = currentUser.userId;
    if (!targetId) {
      CustomAlert.alert("Error", "User identifier (userId) is missing.");
      return;
    }
    setLoading(true);
    try {
      const res = await updateUserFunc(services, {
        userId: targetId,
        displayName: displayName.trim(),
      });
      if (res.status) {
        await refreshUserData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update display name.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates the user's Beta Group assignment via updateUserFunc.
   */
  const handleUpdateBetaGroup = async (): Promise<void> => {
    const targetId = currentUser.userId;
    if (!targetId) {
      CustomAlert.alert("Error", "User identifier (userId) is missing.");
      return;
    }
    setSavingBetaGroup(true);
    setBetaGroupError(null);
    try {
      const cleanGroup = betaGroup.trim().toLowerCase();
      const res = await updateUserFunc(services, {
        userId: targetId,
        betaGroup: cleanGroup,
      });
      if (res.status) {
        setCurrentUser((prev) => ({
          ...prev,
          betaGroup: cleanGroup || undefined,
        }));
        await refreshUserData();
        CustomAlert.alert(
          "Success",
          cleanGroup
            ? `User assigned to Beta Group '${cleanGroup}'.`
            : "User removed from Beta Group."
        );
      } else {
        setBetaGroupError(res.error || "Failed to update Beta Group.");
      }
    } catch (err: any) {
      setBetaGroupError(err.message || "An error occurred.");
    } finally {
      setSavingBetaGroup(false);
    }
  };

  /**
   * Clears the user's Beta Group assignment.
   */
  const handleClearBetaGroup = async (): Promise<void> => {
    const targetId = currentUser.userId;
    if (!targetId) return;
    setSavingBetaGroup(true);
    try {
      const res = await updateUserFunc(services, {
        userId: targetId,
        betaGroup: "",
      });
      if (res.status) {
        setBetaGroup("");
        setCurrentUser((prev) => ({
          ...prev,
          betaGroup: undefined,
        }));
        await refreshUserData();
        CustomAlert.alert("Success", "User removed from Beta Group.");
      } else {
        CustomAlert.alert("Error", res.error || "Failed to clear Beta Group.");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "An error occurred.");
    } finally {
      setSavingBetaGroup(false);
    }
  };

  /**
   * Updates the user's storage quota with inline validation.
   *
   * @returns {Promise<void>}
   */
  const handleUpdateQuota = async (): Promise<void> => {
    const gbVal = parseFloat(quotaGb);
    if (isNaN(gbVal) || gbVal <= 0) {
      setQuotaError("Enter a valid positive number in GB (e.g. 2.0).");
      return;
    }
    setQuotaError(null);
    const targetId = currentUser.userId;
    if (!targetId) {
      CustomAlert.alert("Error", "User identifier (userId) is missing.");
      return;
    }
    setSavingQuota(true);
    try {
      const bytes = Math.round(gbVal * 1024 * 1024 * 1024);
      const res = await updateUserFunc(services, {
        userId: targetId,
        storageQuota: bytes,
      });
      if (res.status) {
        setIsEditingQuota(false);
        await refreshUserData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update storage quota.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setSavingQuota(false);
    }
  };

  /**
   * Recreates or links the All Contacts group for the current user.
   *
   * @returns {Promise<void>}
   */
  const handleRecreateGroup = async (): Promise<void> => {
    const targetId = currentUser.userId;
    if (!targetId) {
      CustomAlert.alert("Error", "User identifier (userId) is missing.");
      return;
    }
    setLoading(true);
    try {
      const res = await ensureAllContactsGroup(services, targetId);
      if (res.status) {
        await refreshUserData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to recreate All Contacts group.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Adds or updates a user claim with inline validation using addUserClaimFunc.
   *
   * @param {string} key - The claim key.
   * @param {string} val - The claim string representation to parse.
   * @returns {Promise<void>}
   */
  const handleAddClaim = async (key: string, val: string): Promise<void> => {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      setClaimKeyError("Claim key cannot be empty.");
      return;
    }
    setClaimKeyError(null);

    let parsedVal: any = val;
    if (val === "true") parsedVal = true;
    else if (val === "false") parsedVal = false;
    else if (!isNaN(Number(val)) && val.trim() !== "") parsedVal = Number(val);

    setLoading(true);
    try {
      const res = await addUserClaimFunc(services, {
        email: currentUser.email,
        password: "",
        claim: JSON.stringify({ [trimmedKey]: parsedVal }),
      });
      if (res.status) {
        setClaimKey("");
        await refreshUserData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to add claim.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Removes a claim from the current user account using removeUserClaimFunc.
   *
   * @param {string} key - The claim key to remove.
   * @returns {Promise<void>}
   */
  const handleRemoveClaim = async (key: string): Promise<void> => {
    setLoading(true);
    try {
      const res = await removeUserClaimFunc(services, {
        email: currentUser.email,
        claimName: key,
      });
      if (res.status) {
        await refreshUserData();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove claim.");
      }
    } catch (error: any) {
      CustomAlert.alert("Error", error.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const memberCount = currentUser.allContactsGroup?.contactIds?.length ?? null;

  return (
    <View style={styles.editorContent}>
      <ThemedProgressBar loading={loading} />

      {/* Account Info Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted, marginBottom: 0 }]}>
            Account Record
          </ThemedText>
          <ThemedKebabMenu
            actions={[
              isUserActive
                ? {
                    id: "deactivate",
                    label: "Deactivate User",
                    icon: "person-remove-outline",
                    destructive: true,
                    onPress: () => confirmToggleStatus(false),
                  }
                : {
                    id: "reactivate",
                    label: "Reactivate User",
                    icon: "person-add-outline",
                    destructive: false,
                    onPress: () => confirmToggleStatus(true),
                  },
            ]}
            compact
            accessibilityLabel={`Actions for ${currentUser.displayName || currentUser.email}`}
          />
        </View>

        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            STATUS
          </ThemedText>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <View
              style={[
                styles.statusBadgeSmall,
                isUserActive
                  ? { backgroundColor: "rgba(16, 185, 129, 0.12)", borderColor: "rgba(16, 185, 129, 0.35)" }
                  : { backgroundColor: "rgba(239, 68, 68, 0.12)", borderColor: "rgba(239, 68, 68, 0.35)" },
              ]}
            >
              <ThemedText
                type="stamp"
                style={{
                  color: isUserActive ? "#059669" : "#DC2626",
                  fontSize: 10,
                  fontWeight: "700",
                }}
              >
                {isUserActive ? "active" : "deactivated"}
              </ThemedText>
            </View>
            <ThemedButton
              title={isUserActive ? "Deactivate" : "Reactivate"}
              variant={isUserActive ? "danger" : "primary"}
              onPress={() => confirmToggleStatus(!isUserActive)}
              style={{ paddingVertical: 4, paddingHorizontal: 10 }}
            />
          </View>
        </View>
        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            EMAIL
          </ThemedText>
          <ThemedText style={styles.infoValue}>{currentUser.email}</ThemedText>
        </View>
        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            USER ID
          </ThemedText>
          <ThemedText type="code" style={[styles.infoValue, { color: textMuted }]}>
            {currentUser.userId}
          </ThemedText>
        </View>
        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            TYPE
          </ThemedText>
          <View
            style={[
              styles.typeBadgeSmall,
              currentUser.userType === "serviceaccount"
                ? {
                  backgroundColor: "rgba(255, 91, 3, 0.12)",
                  borderColor: "rgba(255, 91, 3, 0.35)",
                }
                : {
                  backgroundColor: BlueLinenRange.wash,
                  borderColor: BlueLinenRange.border,
                },
            ]}
          >
            <ThemedText
              type="stamp"
              style={{
                color:
                  currentUser.userType === "serviceaccount"
                    ? BrandColors.sungoldOrange.hex
                    : BlueLinenRange.archival,
                fontSize: 10,
              }}
            >
              {currentUser.userType || "user"}
            </ThemedText>
          </View>
        </View>
        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            AUTHENTICATION
          </ThemedText>
          <ThemedText
            style={[
              styles.infoValue,
              {
                color: currentUser.authProvider === "google" ? "#EA4335" : textMuted,
                fontWeight: "600",
              },
            ]}
          >
            {currentUser.authProvider === "google" ? "Google IDP" : "User / Password"}
          </ThemedText>
        </View>
        <View style={styles.infoRow}>
          <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
            LAST LOGGED ON
          </ThemedText>
          <ThemedText style={styles.infoValue}>
            {currentUser.lastSignInTime
              ? new Date(currentUser.lastSignInTime).toLocaleString()
              : "Never"}
          </ThemedText>
        </View>
      </View>

      {/* Display Name Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
          Display Name
        </ThemedText>
        <ThemedInput
          placeholder="Display Name"
          value={displayName}
          onChangeText={(text) => {
            setDisplayName(text);
            if (displayNameError) setDisplayNameError(null);
          }}
          error={displayNameError}
        />
        <View style={styles.buttonActionRow}>
          <ThemedButton title="Save Name" variant="primary" onPress={handleUpdateName} />
        </View>
      </View>

      {/* Beta Group Configuration Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted, marginBottom: 0 }]}>
            Beta Group Access
          </ThemedText>
          {currentUser.betaGroup ? (
            <View
              style={{
                backgroundColor: "rgba(5, 150, 105, 0.12)",
                borderColor: "#059669",
                borderWidth: 1,
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 6,
              }}
              testID="admin-active-beta-badge"
            >
              <ThemedText style={{ color: "#059669", fontSize: 11, fontWeight: "700" }}>
                Active: {currentUser.betaGroup}
              </ThemedText>
            </View>
          ) : (
            <ThemedText style={{ color: textMuted, fontSize: 11 }}>No Beta Group</ThemedText>
          )}
        </View>

        <ThemedText style={{ fontSize: 12, color: textMuted, marginBottom: 10 }}>
          Assign this user to a beta group to unlock Remote Config custom conditions and early-access platform features.
        </ThemedText>

        <ThemedInput
          placeholder="e.g. beta, early-adopters (or blank to clear)"
          value={betaGroup}
          onChangeText={(val) => {
            setBetaGroup(val);
            if (betaGroupError) setBetaGroupError(null);
          }}
          autoCapitalize="none"
          error={betaGroupError}
          testID="admin-beta-group-input"
        />

        <View style={[styles.buttonActionRow, { gap: 8 }]}>
          <ThemedButton
            title="Save Beta Group"
            variant="primary"
            disabled={savingBetaGroup}
            loading={savingBetaGroup}
            onPress={handleUpdateBetaGroup}
            testID="admin-save-beta-group-btn"
          />
          {currentUser.betaGroup && (
            <ThemedButton
              title="Remove from Beta"
              variant="minimal"
              disabled={savingBetaGroup}
              onPress={() => {
                setBetaGroup("");
                void handleClearBetaGroup();
              }}
              testID="admin-clear-beta-group-btn"
            />
          )}
        </View>
      </View>

      {/* Storage Quota Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted, marginBottom: 0 }]}>
            Storage Quota & Usage
          </ThemedText>
          {!isEditingQuota && (
            <ThemedButton
              title="Edit Quota"
              variant="secondary"
              onPress={() => setIsEditingQuota(true)}
              style={{ paddingVertical: 4, paddingHorizontal: 12 }}
            />
          )}
        </View>

        {quotaSummary ? (() => {
          const rawAllocated = quotaSummary.storageQuotaBytes ?? (quotaSummary as any).quotaBytes ?? DEFAULT_STORAGE_QUOTA_BYTES;
          const allocatedBytes = typeof rawAllocated === "number" && !isNaN(rawAllocated) ? rawAllocated : DEFAULT_STORAGE_QUOTA_BYTES;
          const usedBytes = typeof quotaSummary.usedBytes === "number" && !isNaN(quotaSummary.usedBytes) ? quotaSummary.usedBytes : 0;
          const rawUsagePct = quotaSummary.usagePercentage ?? (quotaSummary as any).percentUsed ?? (allocatedBytes > 0 ? (usedBytes / allocatedBytes) * 100 : 0);
          const safeUsagePct = typeof rawUsagePct === "number" && !isNaN(rawUsagePct) ? rawUsagePct : 0;
          const isOverLimit = safeUsagePct >= 100 || usedBytes >= allocatedBytes;
          const isNearLimit = safeUsagePct > 80;
          const mediaCount = typeof quotaSummary.mediaCount === "number" ? quotaSummary.mediaCount : 0;

          return (
            <View style={styles.quotaDetailsContainer}>
              <View style={styles.infoRow}>
                <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
                  ALLOCATED QUOTA
                </ThemedText>
                <ThemedText style={styles.infoValue}>
                  {formatBytes(allocatedBytes)} ({(((allocatedBytes || 0) / (1024 * 1024 * 1024))).toFixed(1)} GB)
                </ThemedText>
              </View>

              <View style={styles.infoRow}>
                <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
                  STORAGE USED
                </ThemedText>
                <ThemedText
                  style={[
                    styles.infoValue,
                    {
                      color: isOverLimit
                        ? BrandColors.beetRed.hex
                        : isNearLimit
                        ? BrandColors.sungoldOrange.hex
                        : textColor,
                      fontWeight: "600",
                    },
                  ]}
                >
                  {formatBytes(usedBytes)} ({safeUsagePct.toFixed(1)}%)
                </ThemedText>
              </View>

              <View style={styles.infoRow}>
                <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
                  MEDIA FILES
                </ThemedText>
                <ThemedText style={styles.infoValue}>
                  {mediaCount} file{mediaCount !== 1 ? "s" : ""}
                </ThemedText>
              </View>

              {/* Quota Progress Bar */}
              <View style={styles.quotaBarTrack}>
                <View
                  style={[
                    styles.quotaBarFill,
                    {
                      width: `${Math.min(safeUsagePct, 100)}%`,
                      backgroundColor: isOverLimit
                        ? BrandColors.beetRed.hex
                        : isNearLimit
                        ? BrandColors.sungoldOrange.hex
                        : BrandColors.grassyGreen.hex,
                    },
                  ]}
                />
              </View>
            </View>
          );
        })() : (
          <View style={styles.infoRow}>
            <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
              ALLOCATED QUOTA
            </ThemedText>
            <ThemedText style={styles.infoValue}>
              {formatBytes(initialQuotaBytes)} ({(((initialQuotaBytes || 0) / (1024 * 1024 * 1024))).toFixed(1)} GB)
            </ThemedText>
          </View>
        )}

        {isEditingQuota && (
          <View style={styles.updateQuotaSection}>
            <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
              UPDATE QUOTA (GB)
            </ThemedText>

            {/* Preset Buttons */}
            <View style={styles.presetButtonsRow}>
              {QUOTA_TIER_PRESETS.map((preset) => (
                <TouchableOpacity
                  key={preset.gb}
                  style={[
                    styles.presetBtn,
                    { backgroundColor: backgroundElement, borderColor: borderColor },
                    quotaGb === String(preset.gb) && {
                      backgroundColor: "rgba(59, 130, 246, 0.15)",
                      borderColor: "#2563EB",
                    },
                  ]}
                  onPress={() => {
                    setQuotaGb(String(preset.gb));
                    if (quotaError) setQuotaError(null);
                  }}
                >
                  <ThemedText
                    style={[
                      styles.presetBtnText,
                      quotaGb === String(preset.gb) && { color: "#2563EB", fontWeight: "bold" },
                    ]}
                  >
                    {preset.label}
                  </ThemedText>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.updateQuotaRow}>
              <View style={{ flex: 1 }}>
                <ThemedInput
                  placeholder="Quota in GB (e.g. 5.0)"
                  value={quotaGb}
                  onChangeText={(text) => {
                    setQuotaGb(text);
                    if (quotaError) setQuotaError(null);
                  }}
                  keyboardType="numeric"
                  error={quotaError}
                />
              </View>
              <ThemedButton
                title={savingQuota ? "Saving…" : "Save Quota"}
                variant="primary"
                disabled={savingQuota}
                onPress={handleUpdateQuota}
              />
              <ThemedButton
                title="Cancel"
                variant="minimal"
                disabled={savingQuota}
                onPress={() => {
                  setQuotaError(null);
                  setIsEditingQuota(false);
                }}
              />
            </View>
          </View>
        )}
      </View>

      {/* All Contacts Group Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
          All Contacts Group
        </ThemedText>
        {currentUser.allContactsGroupId ? (
          <View style={styles.groupDetailsEditor}>
            <View style={styles.infoRow}>
              <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
                GROUP ID
              </ThemedText>
              <ThemedText type="code" style={[styles.infoValue, { color: textMuted }]}>
                {currentUser.allContactsGroupId}
              </ThemedText>
            </View>
            <View style={styles.infoRow}>
              <ThemedText type="stamp" style={[styles.infoLabel, { color: textMuted }]}>
                MEMBERS
              </ThemedText>
              <ThemedText
                style={[
                  styles.infoValue,
                  { color: BrandColors.grassyGreen.hex, fontWeight: "600" },
                ]}
              >
                {memberCount !== null
                  ? `${memberCount} member${memberCount !== 1 ? "s" : ""}`
                  : "loading…"}
              </ThemedText>
            </View>
          </View>
        ) : (
          <View style={styles.missingGroupContainer}>
            <ThemedText style={[styles.missingText, { color: BrandColors.beetRed.hex }]}>
              No All Contacts group linked to this account.
            </ThemedText>
            <View style={styles.buttonActionRow}>
              <ThemedButton
                title="Recreate Group"
                variant="secondary"
                onPress={handleRecreateGroup}
              />
            </View>
          </View>
        )}
      </View>

      {/* User Claims Card */}
      <View style={[styles.editorCard, { backgroundColor: cardBg, borderColor: borderColor }]}>
        <ThemedText type="stamp" style={[styles.sectionLabel, { color: textMuted }]}>
          User Claims
        </ThemedText>

        <View style={styles.claimsList}>
          {currentUser.claims && Object.keys(currentUser.claims).length > 0 ? (
            Object.entries(currentUser.claims).map(([key, val]) => (
              <View
                key={key}
                style={[
                  styles.editorClaimBadge,
                  { backgroundColor: inputReadOnlyBg, borderColor: inputReadOnlyBorder },
                ]}
              >
                <View style={{ flex: 1, gap: 2 }}>
                  <ThemedText
                    type="stamp"
                    style={{ fontSize: 10, color: inputReadOnlyTextColor }}
                  >
                    {key}
                  </ThemedText>
                  <ThemedText style={[styles.claimText, { color: textColor }]}>
                    {Array.isArray(val) ? val.join(", ") : String(val)}
                  </ThemedText>
                </View>
                <TouchableOpacity
                  onPress={() => handleRemoveClaim(key)}
                  style={styles.removeClaimBtn}
                  activeOpacity={0.7}
                  accessibilityLabel={`Remove claim ${key}`}
                >
                  <FontAwesome name="trash" size={14} color={BrandColors.beetRed.hex} />
                </TouchableOpacity>
              </View>
            ))
          ) : (
            <ThemedText style={[styles.noClaims, { color: textMuted }]}>
              No custom claims assigned.
            </ThemedText>
          )}
        </View>

        {/* Add Claim Form */}
        <View style={[styles.addClaimForm, { borderTopColor: borderColor }]}>
          <ThemedText
            type="stamp"
            style={[styles.sectionLabel, { marginTop: 8, color: textMuted }]}
          >
            Add New Claim
          </ThemedText>
          <ThemedInput
            placeholder="Claim Key (e.g. admin)"
            value={claimKey}
            onChangeText={(text) => {
              setClaimKey(text);
              if (claimKeyError) setClaimKeyError(null);
            }}
            error={claimKeyError}
          />
          <ThemedInput
            placeholder="Claim Value (e.g. true)"
            value={claimValue}
            onChangeText={setClaimValue}
          />
          <View style={styles.buttonActionRow}>
            <ThemedButton
              title="Add Claim"
              variant="primary"
              onPress={() => handleAddClaim(claimKey, claimValue)}
            />
          </View>

          {/* Role Presets */}
          <ThemedText
            type="stamp"
            style={[styles.sectionLabel, { marginTop: 16, color: textMuted }]}
          >
            Role Presets
          </ThemedText>
          <View style={styles.presetButtonsRow}>
            {ROLE_PRESETS.map((preset) => {
              const hasClaim = currentUser.claims && currentUser.claims[preset.key] !== undefined;
              return (
                <TouchableOpacity
                  key={preset.key}
                  onPress={() => {
                    if (hasClaim) {
                      handleRemoveClaim(preset.key);
                    } else {
                      handleAddClaim(preset.key, "true");
                    }
                  }}
                  style={[
                    styles.presetBtn,
                    {
                      backgroundColor: hasClaim ? "rgba(74, 96, 143, 0.2)" : BlueLinenRange.wash,
                      borderColor: hasClaim ? BlueLinenRange.archival : BlueLinenRange.border,
                    },
                  ]}
                  activeOpacity={0.7}
                  accessibilityLabel={`${hasClaim ? "Remove" : "Add"} ${preset.label} role preset`}
                >
                  <ThemedText
                    type="stamp"
                    style={[
                      styles.presetBtnText,
                      {
                        color: hasClaim ? BlueLinenRange.archival : BlueLinenRange.deepInk,
                        fontWeight: hasClaim ? "700" : "600",
                      },
                    ]}
                  >
                    {hasClaim ? `✓ ${preset.label}` : `+ ${preset.label}`}
                  </ThemedText>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
  },
  actionHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  addUserHeaderBtn: {
    marginVertical: 0,
    paddingHorizontal: 14,
  },
  countText: {
    fontSize: 11,
    marginTop: 2,
  },
  listContent: {
    paddingBottom: 28,
    gap: 8,
  },
  emptyContainer: {
    marginTop: 48,
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 24,
  },
  emptyStamp: {
    fontSize: 11,
  },
  emptyText: {
    textAlign: "center",
    fontStyle: "italic",
    fontSize: 14,
    lineHeight: 20,
  },

  // Card
  card: {
    padding: 8,
    borderRadius: 0,
    borderWidth: 1,
    gap: 6,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  titleArea: {
    flex: 1,
    gap: 2,
  },
  cardTitle: {
    fontSize: 16,
  },
  emailText: {
    fontSize: 13,
  },
  idText: {
    fontSize: 11,
  },

  // Type badge
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  typeBadgeSmall: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  typeBadgeText: {
    fontSize: 10,
  },

  // All Contacts Group
  groupRow: {
    borderRadius: 0,
    borderWidth: 1,
    padding: 8,
  },
  groupInfo: {
    gap: 4,
  },
  sectionLabel: {
    fontSize: 10,
    marginBottom: 2,
  },
  groupDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  groupId: {
    fontSize: 11,
    flex: 1,
  },
  memberCountBadge: {
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderWidth: 1,
  },
  memberCountText: {
    fontSize: 11,
    fontWeight: "700",
  },
  missingText: {
    fontSize: 12,
    fontStyle: "italic",
  },
  createGroupRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 2,
  },
  createGroupBtn: {
    marginVertical: 0,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },

  // Claims
  claimsSection: {
    gap: 2,
  },
  claimsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 3,
  },
  claimBadge: {
    borderRadius: 3,
    paddingHorizontal: 3,
    paddingVertical: 1,
    borderWidth: 1,
    flexDirection: "row",
    gap: 2,
    alignItems: "center",
  },
  claimKey: {
    fontSize: 9.5,
    fontWeight: "700",
    lineHeight: 12,
  },
  claimValue: {
    fontSize: 9.5,
    lineHeight: 12,
  },
  noClaims: {
    fontSize: 10,
    fontStyle: "italic",
  },

  // Footer
  cardFooter: {
    borderTopWidth: 1,
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  dateText: {
    fontSize: 10,
  },

  // Editor styles
  editorContent: {
    gap: 14,
    paddingBottom: 36,
  },
  editorCard: {
    padding: 12,
    borderRadius: 0,
    borderWidth: 1,
    gap: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 4,
    gap: 8,
  },
  infoLabel: {
    fontSize: 11,
  },
  infoValue: {
    fontSize: 13,
  },
  groupDetailsEditor: {
    gap: 2,
  },
  missingGroupContainer: {
    gap: 8,
  },
  buttonActionRow: {
    marginTop: 6,
    alignItems: "flex-start",
  },
  // Storage Quota
  quotaDetailsContainer: {
    gap: 8,
  },
  quotaBarTrack: {
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    marginTop: 4,
  },
  quotaBarFill: {
    height: "100%",
    borderRadius: 4,
  },
  updateQuotaSection: {
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: "rgba(0, 0, 0, 0.06)",
    paddingTop: 10,
  },
  updateQuotaRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  claimsList: {
    gap: 8,
    marginVertical: 4,
  },
  editorClaimBadge: {
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  claimText: {
    fontSize: 13,
  },
  removeClaimBtn: {
    padding: 6,
  },
  addClaimForm: {
    gap: 10,
    marginTop: 8,
    borderTopWidth: 1,
    paddingTop: 12,
  },
  presetButtonsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  presetBtn: {
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
  },
  presetBtnText: {
    fontSize: 11,
  },
  compactModeCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 4,
    paddingVertical: 4,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 30,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  compactModeTitle: {
    fontSize: 10,
    textAlign: "center",
  },
  detailsRowCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 0,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsRowLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 2,
    gap: 8,
  },
  detailsRowIcon: {
    fontSize: 18,
  },
  detailsRowUser: {
    flex: 1,
  },
  detailsRowMiddle: {
    flex: 1.5,
    paddingHorizontal: 8,
  },
  detailsSubText: {
    fontSize: 10,
    marginTop: 1,
  },
  detailsRowRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  cardHeaderActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  statusBadgeSmall: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "center",
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  statusBadgeTextSmall: {
    fontSize: 9,
    fontWeight: "700",
  },
  authBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "flex-start",
  },
  authBadgeSmall: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: "center",
  },
  authBadgeText: {
    fontSize: 10,
    fontWeight: "700",
  },
  authBadgeTextSmall: {
    fontSize: 9,
    fontWeight: "700",
  },
});
